import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy, DOCUMENT, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { I18nService } from 'src/app/core/services/i18n/i18n.service';
import { map, shareReplay, take } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { UtilService } from 'src/app/core/services/util/util.service';
import { CookieService } from 'ngx-cookie-service';
import { AppGlobalsModule } from 'src/app/app-globals.module';
import { StorageService } from 'src/app/core/services/storage/storage.service';
import { BrowserPlatformService } from 'src/app/core/services/browser-platform/browser-platform.service';

import { ReplaySubject } from 'rxjs';
import { SelctableItem } from 'src/app/shared/select-item/select-item.component';
import { UrlAddressService } from 'src/app/core/services/url-address/url-address.service';
import type { CivilDate, DayJson, MonthJson, PickerMonthJson, ShabatotYearJson } from '../calendar-page/calendar-api.types';

const DEFAULT_PLACE = 212;
const JERUSALEM_PLACE = 156;

@Injectable({
    providedIn: 'root',
})
export class CalendarService implements OnDestroy {
    private _http = inject(HttpClient);
    private _util = inject(UtilService);
    private cookieService = inject(CookieService);
    private _i18n = inject(I18nService);
    private _domSanitizer = inject(DomSanitizer);
    private _doc = inject<Document>(DOCUMENT);
    private _url = inject(UrlAddressService);
    private _storage = inject(StorageService);
    private _browser = inject(BrowserPlatformService);

    //the following variable is also called calendarCacheVersion in _CalendarEsi.all.cshtml

    //get the cache version that should be set in the dotnet layout in production
    readonly CALENDAR_CACHE_VERSION = (this._browser.nativeWindow as (Window & { calendarCacheVersion?: number }) | null)?.calendarCacheVersion ?? 39;

    public isHeb: string;
    readonly PLACES_API_URL;
    readonly ALL_DAILY_TIMES = AppGlobalsModule.apiPath('times/AllDailyTimes');
    readonly SHABBAT_TIMES_API_URL = AppGlobalsModule.apiPath('times/shabbattimes');
    readonly PESACH_TIMES_API_URL = AppGlobalsModule.apiPath('calendar/pesachtimes');
    readonly MEMORIAL_API_URL = AppGlobalsModule.apiPath('calendar/memorial');

    readonly FIRST_OF_MONTH: number = 1;
    private _currLang = 'heb';
    readonly BASE_HEB_MONTHS: HebMonth[] = [
        { key: HebMonths.Tishri, value: $localize`:@@Tishri:תשרי` },
        { key: HebMonths.Heshvan, value: $localize`:@@Heshvan:מרחשוון` },
        { key: HebMonths.Kislev, value: $localize`:@@Kislev:כסלו` },
        { key: HebMonths.Tevet, value: $localize`:@@Tevet:טבת` },
        { key: HebMonths.Shevat, value: $localize`:@@Shevat:שבט` },
        { key: HebMonths.Nisan, value: $localize`:@@Nisan:ניסן` },
        { key: HebMonths.Iyar, value: $localize`:@@Iyar:אייר` },
        { key: HebMonths.Sivan, value: $localize`:@@Sivan:סיוון` },
        { key: HebMonths.Tamuz, value: $localize`:@@Tamuz:תמוז` },
        { key: HebMonths.Av, value: $localize`:@@Av:מנחם אב` },
        { key: HebMonths.Elul, value: $localize`:@@Elul:אלול` },
    ];

    readonly ALL_HEB_MONTHS: HebMonth[] = [
        ...this.BASE_HEB_MONTHS.slice(0, 5),
        { key: HebMonths.Adar, value: $localize`:@@Adar:אדר` },
        { key: HebMonths.AdarI, value: $localize`:@@Adar-I:אדר א'` },
        { key: HebMonths.AdarII, value: $localize`:@@Adar-II:אדר ב'` },
        ...this.BASE_HEB_MONTHS.slice(5),
    ];

    readonly leapYearMonths: HebMonth[] = [
        ...this.BASE_HEB_MONTHS.slice(0, 5),
        { key: HebMonths.AdarI, value: $localize`:@@Adar-I:אדר א'` },
        { key: HebMonths.AdarII, value: $localize`:@@Adar-II:אדר ב'` },
        ...this.BASE_HEB_MONTHS.slice(5),
    ];

    readonly nonLeapYearMonths: HebMonth[] = [
        ...this.BASE_HEB_MONTHS.slice(0, 5),
        { key: HebMonths.Adar, value: $localize`:@@Adar:אדר` },
        ...this.BASE_HEB_MONTHS.slice(5),
    ];

    public daysOfWeek: string[];
    readonly ENG_MONTHS: EngMonth[] = [
        { key: EngMonths.January, value: $localize`:@@January:ינואר` },
        { key: EngMonths.February, value: $localize`:@@February:פברואר` },
        { key: EngMonths.March, value: $localize`:@@March:מרץ` },
        { key: EngMonths.April, value: $localize`:@@April:אפריל` },
        { key: EngMonths.May, value: $localize`:@@May:מאי` },
        { key: EngMonths.June, value: $localize`:@@June:יוני` },
        { key: EngMonths.July, value: $localize`:@@July:יולי` },
        { key: EngMonths.August, value: $localize`:@@August:אוגוסט` },
        { key: EngMonths.September, value: $localize`:@@September:ספטמבר` },
        { key: EngMonths.October, value: $localize`:@@October:אוקטובר` },
        { key: EngMonths.November, value: $localize`:@@November:נובמבר` },
        { key: EngMonths.December, value: $localize`:@@December:דצמבר` },
    ];

    readonly dedicationDatePickerTitles: DedicationDatePickerTitle[] = [
        {
            key: DedicationType.DAILY_HALACHA,
            value: $localize`:@@dailyHalachaDediTitle:בחירת תאריך הקדשה של הלכה יומית`,
        },
        { key: DedicationType.KADDISH_MONTH, value: $localize`:@@kaddishDediTitle:בחירת תאריך פטירה` },
        {
            key: DedicationType.SITE_DAILY,
            value: $localize`:@@siteSponsorDediTitle:בחירת תאריך הקדשה של נציב היום`,
        },
        {
            key: DedicationType.SITE_PREMIUM,
            value: $localize`:@@siteMakerDediTitle:בחירת תאריך התחלת ההקדשה של נציב האתר`,
        },
        { key: DedicationType.NONE, value: '' },
    ];
    /**
     *
     * @param operation
     * @param year
     * @param month
     * @param currDay
     * @param place
     * @param chagTimes
     * @returns
     */
    private _getCalApi(operation: Operation, year: number, month: number, currDay = this.FIRST_OF_MONTH, place = DEFAULT_PLACE, chagTimes = true, heb = true) {
        let [hb, js] = ['', ''];
        if (operation === Operation.MD) {
            [hb, js] = [`&hb=${heb}`, '&js=true'];
        }
        this._currLang = this._i18n.getLang();
        const code = this.getCode(operation, month, year, place, currDay, this._currLang);
        return AppGlobalsModule.apiPath(
            `/calendar/calaj.aspx?cacheVer=${this.CALENDAR_CACHE_VERSION}&v=1&op=${operation}&pl=${place}&yr=${year}&mn=${month}&dy=${currDay}${hb}&sv=false&lng=${
                this._currLang
            }${js}&chagTimes=${chagTimes}&x=${this.getCode(operation, month, year, place, currDay, this._currLang)}`,
            '',
            '',
        );
    }
    private _getBarMitzvaApi(type: number, year: number, month: string, day: number, calendarType: CalendarType) {
        this._currLang = this._i18n.getLang();
        const code = this.getCodeBarMitzva(type, month, year, day, this._currLang);
        const calendarTypeNum = calendarType === CalendarType.HEBREW ? 0 : 1;
        return AppGlobalsModule.apiPath(`calendar/calculate?type=${type}&year=${year}&month=${month}&day=${day}&calendarType=${calendarTypeNum}&x=${code}`);
    }

    public getOmer(): Observable<string | null> {
        return this.getCurrDayTimesAllData().pipe(map((data) => data?.extraDateData?.omer ?? null));
    }

    public getHebDateApi(): Observable<string | null> {
        return this.getCurrDayTimesAllData().pipe(map((data) => data?.extraDateData?.hebDate ?? null));
    }

    /*
     * @param operation
     * @param year
     * @param month
     * @param place --- this can be either the place id or the place name
     * @param day must be <= 0 to ignore day
     * @param lng must be 3 charachter like heb or eng
     * @returns code
     */

    private getCode(operation: string, month: number, year: number, place: number, day: number, lng: string) {
        const lastCharOp = this._lastChar(operation);
        const lastCharMn = this._lastChar(month);
        const lastCharYr = this._lastChar(year);
        const lastCharPl = this._lastChar(place);
        const lastCharDy = day <= 0 ? '' : this._lastChar(day);
        const lastCharLng = this._lastChar(lng);
        return `r${lastCharOp}${lastCharMn}h${lastCharYr}d${lastCharPl}${lastCharDy}k${lastCharLng}`;
    }
    private getCodeBarMitzva(type: number, month: string, year: number, day: number, lng: string) {
        const lastCharOp = this._lastChar(type);
        const lastCharMn = this._lastChar(month);
        const lastCharYr = this._lastChar(year);
        const lastCharDy = day <= 0 ? '' : this._lastChar(day);
        const lastCharLng = this._lastChar(lng);
        return `r${lastCharOp}${lastCharMn}h${lastCharYr}d${lastCharDy}k${lastCharLng}`;
    }

    private _lastChar(str: string | number): string {
        str = str.toString();
        return str[str.length - 1];
    }

    private _subscriptions: Subscription[] = [];
    private _places$: Observable<PlaceLatLon[]>;

    constructor() {
        this.daysOfWeek = this._i18n.langIsHeb() ? ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        // Seed clock skew from localStorage so early scheduling decisions (before the first response lands)
        // use a recent value. Recomputed on every API response, so this is purely an optimization.
        if (!AppGlobalsModule.isServer) {
            try {
                const seeded = this._storage.load(CalendarService.CLOCK_SKEW_STORAGE_KEY);
                if (typeof seeded === 'number' && isFinite(seeded)) {
                    this._clockSkewMs = seeded;
                }
            } catch {
                // localStorage unavailable; fall back to in-memory zero until first response.
            }
        }

        this.loadDayTimesAllData();
        this.isHeb = this._i18n.langIsHeb() ? 'heb' : 'eng';
        this.PLACES_API_URL = AppGlobalsModule.apiPath(`/calendar/calaj.aspx?cacheVer=${this.CALENDAR_CACHE_VERSION}&lng=${this.isHeb}&v=1&op=pl`, '', '');
        // Fetched once per app, Israel first and each half sorted by name; replays to late subscribers.
        this._places$ = this._http.get<PlaceLatLon[]>(this.PLACES_API_URL).pipe(
            map((places) =>
                places
                    .filter((place) => place.il === 'true')
                    .sort((a, b) => (a.name > b.name ? 1 : -1))
                    .concat(places.filter((place) => place.il === 'false').sort((a, b) => (a.name > b.name ? 1 : -1))),
            ),
            shareReplay(1),
        );
    }

    private _currDatepickerCal$ = new ReplaySubject<any>(1);
    public getCurrDatepickerCal(): Observable<any> {
        return this._currDatepickerCal$.asObservable();
    }
    private _setCurrDatepickerCal(cal: any): void {
        this._currDatepickerCal$.next(cal);
    }

    public loadDatePickerCal({ year, month } = this.getHebDate(), dedicationType: DedicationType, lang?: string): void {
        this._setDediDayMapsPerCurrAndNextMonth(null);

        this._subscriptions.push(
            this._http
                .get<any>(this._getCalApi(Operation.MD, year, month))
                .pipe(take(2)) //must be 2 so will run in universal and client
                .subscribe((cal: any) => {
                    this._setCurrDatepickerCal(cal);
                    if (cal && cal.date && dedicationType !== DedicationType.NONE) {
                        const { year, month } = cal.date.heb;
                        this._getDayDediCalMap(year, month, dedicationType, lang);
                    }
                }),
        );
    }

    /**
     *
     * @returns
     */
    public getStandardTimesByEngDate(
        year: number = new Date().getFullYear(),
        month: number = new Date().getMonth() + 1,
        currDay: number = new Date().getDate(),
    ): Observable<StandardTimes> {
        return this._http.get<StandardTimes>(this._getCalApi(Operation.DJ, year, month, currDay, JERUSALEM_PLACE, false));
    }

    /**
     *
     * @returns
     */
    public getStandardTimesByHebDate(year: number, month: number, currDay: number): Observable<StandardTimes> {
        return this._http.get<any>(this._getCalApi(Operation.MD, year, month, currDay, JERUSALEM_PLACE, false)).pipe(map((monthJson) => monthJson.times));
    }

    public getMoladotHebDate(year: number, month: number, currDay: number): Observable<StandardTimes> {
        return this._http.get<any>(this._getCalApi(Operation.ML, year, month, currDay, JERUSALEM_PLACE, false)).pipe(map((monthJson) => monthJson.times));
    }

    /** the bar mitzva date, its shabbat and that shabbat's parasha/haftara for a birth date (api/calendar/calculate) */
    public getBarMitzva(year: number, month: number, day: number, calendarType: CalendarType): Observable<BarMitzvaData> {
        return this._http.get<BarMitzvaData>(this._getBarMitzvaApi(0, year, String(month), day, calendarType));
    }

    public getEngMonths(): EngMonth[] {
        return this.ENG_MONTHS;
    }

    public getDefaultPlaceId(): number {
        const timesFromCookies = this.cookieService.get('times');
        return timesFromCookies ? +timesFromCookies.split('&')[0].split('=')[1] : 156;
    }

    private _dailyTimeInterval: NodeJS.Timeout | null = null;
    // Timestamp of the last push through _currDayTimesAllData$. Used by the 1s watchdog below
    // to detect throttled/paused intervals (e.g. backgrounded tab, sleeping laptop): if the
    // last push was >= STALE_DATA_THRESHOLD_MS ago, push immediately on the next tick.
    private _lastDataPushAt = 0;
    private static readonly TICK_INTERVAL_MS = 1000;
    private static readonly STALE_DATA_THRESHOLD_MS = 20000;

    // Boundary scheduling: refetch from the server precisely at shkia and midnight, since
    // those are the only times the API's data actually changes. _clockSkewMs corrects for
    // browser clock drift using the HTTP `Date` response header (see _updateClockSkew).
    private _clockSkewMs = 0;
    private _boundaryTimeout: ReturnType<typeof setTimeout> | null = null;
    private _boundaryPoller: ReturnType<typeof setInterval> | null = null;
    private _boundaryPollStartedAt = 0;
    private _hebDateAtBoundaryStart: string | null = null;
    // Flip to true once the server rolls Hebrew date at the selected place's midnight
    // (today it rolls at Israel midnight, so we schedule against Israel time by default).
    private static readonly USE_PLACE_MIDNIGHT = false;
    private static readonly BOUNDARY_POLL_INTERVAL_MS = 20000;
    private static readonly BOUNDARY_POLL_MAX_DURATION_MS = 5 * 60 * 1000;
    private static readonly CLOCK_SKEW_STORAGE_KEY = 'calendarClockSkewMs';
    // Defensive guard for _updateClockSkew: a measured skew larger than this is treated as a
    // broken/stale timestamp source (e.g. a `Date` header that slipped through WITHOUT edge
    // re-stamping, carrying the cache age instead of real time) rather than a genuine clock.
    // Real device drift is seconds-to-minutes; a real multi-hour error would make the whole
    // day's zmanim visibly wrong anyway. We'd rather keep the previous skew than let a
    // cache/misconfig artifact silently corrupt boundary scheduling — the exact failure this
    // whole change fixes. Trade-off: a device whose clock is genuinely off by >6h stays
    // uncorrected (extremely rare, and already glaringly broken to the user).
    private static readonly MAX_PLAUSIBLE_SKEW_MS = 6 * 60 * 60 * 1000;
    // Israel timezone, used when USE_PLACE_MIDNIGHT is false.
    private static readonly ISRAEL_TZ_HOURS = 2;
    public loadDayTimesAllData(
        place?: PlaceLatLon,
        isFromState: boolean = false,
        isForUpdatePlaceFavCookie: boolean = true,
        idPlace?: number, //If you use an ID, you cannot update the cookie because it is not known whether it is in Israel or not.
        // When true, don't emit null before the fetch. Used by background refresh so subscribers
        // that assign the value directly (e.g. main-home-page) don't flicker to an empty state.
        skipNullFlush: boolean = false,
        // When true, bypass the in-process CachingInterceptor so the response carries fresh headers.
        // Default true: clock-skew correctness requires a current `Date` header, which an in-memory
        // replay can't provide (it freezes the headers at fetch time). The CDN caches still apply at
        // the network layer — harmless, since the edge re-stamps `Date` on every response.
        bypassCache: boolean = true,
    ) {
        // console.log('loadDayTimesAllData ', idPlace);
        let placeId: number;
        if (idPlace) {
            placeId = idPlace;
        } else if (place) {
            if (isForUpdatePlaceFavCookie) {
                this._updatePlaceFavCookie(place);
            }
            placeId = +place.id;
        } else {
            placeId = this.getDefaultPlaceId();
        }
        const { value } = this._currDayTimesAllData$;
        if (!isFromState && this._dailyTimeInterval) {
            clearInterval(this._dailyTimeInterval);
        }
        if (value && isFromState) {
            this._setCurrDayTimesAllData(value);
        } else {
            if (!skipNullFlush) {
                this._setCurrDayTimesAllData(null);
            }
            const headers = bypassCache ? new HttpHeaders({ noCache: 'true' }) : undefined;
            this._subscriptions.push(
                this._http
                    .get<DayTimesAllData>(
                        `${this.ALL_DAILY_TIMES}?cacheVer=${this.CALENDAR_CACHE_VERSION}&place=${placeId}`, /////&dateAddOn=-30
                        { observe: 'response', headers },
                    )
                    .pipe(take(1))
                    .subscribe((response: HttpResponse<DayTimesAllData>) => {
                        if (this._dailyTimeInterval) {
                            clearInterval(this._dailyTimeInterval);
                            this._dailyTimeInterval = null;
                        }
                        const dayTimesAllData = response.body;
                        if (dayTimesAllData) {
                            this._updateClockSkew(response);
                            this._setCurrDayTimesAllData(dayTimesAllData);
                            if (!AppGlobalsModule.isServer) {
                                // 1s watchdog: tick cheaply every second, but only actually push
                                // (which triggers change detection page-wide) when the last push
                                // was >= STALE_DATA_THRESHOLD_MS ago. Catches throttled/paused
                                // intervals from backgrounded tabs or machine sleep within 1s.
                                this._dailyTimeInterval = setInterval(() => {
                                    // Re-push the subject's CURRENT value, not this closure's response —
                                    // boundary refetches replace the data without recreating this interval,
                                    // and a captured object would clobber them with pre-rollover times.
                                    const current = this._currDayTimesAllData$.value;
                                    if (current && Date.now() - this._lastDataPushAt >= CalendarService.STALE_DATA_THRESHOLD_MS) {
                                        this._setCurrDayTimesAllData(current);
                                    }
                                }, CalendarService.TICK_INTERVAL_MS);
                                this._scheduleNextBoundary();
                            }
                        }
                    }),
            );
        }
    }

    // Clock skew, place-time, and boundary scheduling
    // -----------------------------------------------
    // Two independent freshness mechanisms operate on the cached daily times:
    //   1. The 1-second watchdog (set up inside loadDayTimesAllData's success branch) re-pushes the
    //      cached object every ~20s so subscribers re-tag modeTime ("closest next" etc.) against the
    //      advancing clock. This is the cheap, in-tab path — no network.
    //   2. Boundary scheduling (this section) refetches from the server precisely at shkia and midnight,
    //      because those are the only two moments per day when the server's data actually changes.
    //      Between boundaries, the cached `standardTimes.times` array already has tomorrow's-arc
    //      correctly — no refetch needed.
    //
    // For (2) to schedule against the right wall-time, we can't trust the browser clock — a user with
    // a skewed system clock would see boundaries fire at the wrong real-world moment. Skew is measured
    // from the HTTP `Date` response header, which the edge (Cloudflare) stamps fresh on every response —
    // including Varnish cache hits and stale-while-revalidate bodies. The response BODY's `datetime`
    // field must never be used for this: bodies are served from cache for up to an hour (plus multi-hour
    // stale-while-revalidate/grace windows) and the edge strips the `Age` header, so a body-derived skew
    // silently imports the cache age of the response and the header clock runs hours behind real time
    // (the "closest daily time already passed" bug, 2026-07).

    private _updateClockSkew(response: HttpResponse<DayTimesAllData>): void {
        // Skew = server "now" minus browser "now", both UTC epoch ms. _placeNow() adds the place tz
        // on top, so switching the source from body-datetime (place frame) to the `Date` header
        // (UTC frame) keeps the same overall frame — the tz terms cancelled out in the old formula.
        const dateHeader = response.headers.get('Date');
        if (!dateHeader) return; // keep the previous skew (seeded or from an earlier response)
        const generatedMs = Date.parse(dateHeader);
        if (isNaN(generatedMs)) return;
        // AllDailyTimes is cacheable for an hour (`max-age=3600`), and a cached copy replays the
        // `Date` of the moment it was GENERATED: without the corrections below the cache age read
        // as negative skew (the beta calendar's hero clock ran 18 minutes slow, 2026-11-02).
        // A shared cache (varnish in production) adds `Age` = seconds since generation (RFC 9111).
        const ageMs = (parseInt(response.headers.get('Age') ?? '0', 10) || 0) * 1000;
        // The browser's own cache adds no `Age`: a replay is recognised by Resource Timing, whose
        // transferSize is 0 for a resource served from the local cache. No entry → treat as fresh.
        if (typeof performance !== 'undefined' && typeof performance.getEntriesByName === 'function' && response.url) {
            const entries = performance.getEntriesByName(response.url) as PerformanceResourceTiming[];
            const last = entries[entries.length - 1];
            if (last && last.transferSize === 0) return;
        }
        const skewMs = generatedMs + ageMs - Date.now();
        // Defensive clamp — see MAX_PLAUSIBLE_SKEW_MS. Reject an implausibly large skew and
        // keep the previous value rather than trust a likely-broken/stale timestamp source.
        if (Math.abs(skewMs) > CalendarService.MAX_PLAUSIBLE_SKEW_MS) return;
        this._clockSkewMs = skewMs;
        if (!AppGlobalsModule.isServer) {
            try {
                this._storage.store(CalendarService.CLOCK_SKEW_STORAGE_KEY, this._clockSkewMs);
            } catch {
                // localStorage unavailable (private mode, quota) — in-memory value is sufficient,
                // and it's recomputed on every API response anyway.
            }
        }
    }

    // Returns the place's tz offset in ms, or null if the place data lacks the timezone field
    // (cached responses from before the server-side rollout). Callers must treat null as
    // "I can't do place-time arithmetic" and fall back to legacy behavior.
    private _placeTzOffsetMs(place: BasePlace | undefined | null): number | null {
        if (!place || place.timezone == null) return null;
        const tz = +place.timezone;
        if (!isFinite(tz)) return null;
        // dstDiff > 0 in the response means "this place is currently observing DST."
        const dst = +(place.dstDiff ?? 0) || 0;
        return (tz + (dst > 0 ? dst : 0)) * 3600_000;
    }

    /**
     * The clock data of a place - its timezone (and the server's `Date`, folded into the skew) -
     * WITHOUT touching the shared current-day state the home page and the sticky header show:
     * the calendar beta asks for any place from its url while the user's saved place stays theirs.
     */
    public fetchPlaceClock(placeId: number): Observable<BasePlace | null> {
        return this._http
            .get<DayTimesAllData>(`${this.ALL_DAILY_TIMES}?cacheVer=${this.CALENDAR_CACHE_VERSION}&place=${placeId}`, {
                observe: 'response',
                headers: new HttpHeaders({ noCache: 'true' }),
            })
            .pipe(
                map((response) => {
                    this._updateClockSkew(response);
                    return response.body?.standardTimes?.place ?? null;
                }),
            );
    }

    /**
     * Place-local "now" for a place that carries its timezone (an `AllDailyTimes` answer's
     * `standardTimes.place`), as wall-clock-as-if-UTC ms; null when the place has no timezone.
     * The calendar beta's hero reads the clock through this (docs/agents/calendar-port.md decision 1).
     */
    public placeNowFor(place: BasePlace | undefined | null): number | null {
        const tzOffsetMs = this._placeTzOffsetMs(place);
        return tzOffsetMs === null ? null : Date.now() + this._clockSkewMs + tzOffsetMs;
    }

    // Convenience for the currently-displayed place. See _placeTzOffsetMs for null semantics.
    private _currentPlaceTzOffsetMs(): number | null {
        return this._placeTzOffsetMs(this._currDayTimesAllData$.value?.standardTimes?.place);
    }

    // Returns place-local "wall-clock-as-if-UTC" ms-since-epoch, or null if the place data
    // doesn't carry timezone info yet (cached pre-rollout response). Callers fall back accordingly.
    private _placeNow(): number | null {
        const tzOffsetMs = this._currentPlaceTzOffsetMs();
        if (tzOffsetMs === null) return null;
        return Date.now() + this._clockSkewMs + tzOffsetMs;
    }

    // Arms a setTimeout for the soonest of {next shkia today, next midnight}. When it fires,
    // _startBoundaryPolling kicks in. Called after every successful fetch so each refetch
    // schedules its own next boundary.
    private _scheduleNextBoundary(): void {
        if (AppGlobalsModule.isServer) return;
        if (this._boundaryTimeout) {
            clearTimeout(this._boundaryTimeout);
            this._boundaryTimeout = null;
        }
        const data = this._currDayTimesAllData$.value;
        if (!data) return;

        // Cached pre-rollout response without tz fields: skip scheduling. The 1s watchdog still
        // handles in-tab freshness; we just don't get the precise shkia/midnight refetches until
        // the next response carries the new fields.
        const nowPlaceMs = this._placeNow();
        if (nowPlaceMs === null) return;

        const shkiaStr = data.standardTimes?.shabat?.skiah; // "HH:MM" in place-local time today
        const shkiaMs = shkiaStr ? this._parseTimeTodayPlaceMs(shkiaStr, nowPlaceMs) : null;
        const midnightMs = this._nextMidnightPlaceMs(nowPlaceMs);
        if (midnightMs === null) return;

        const candidates: number[] = [];
        if (shkiaMs !== null && shkiaMs > nowPlaceMs) candidates.push(shkiaMs);
        if (midnightMs > nowPlaceMs) candidates.push(midnightMs);
        if (!candidates.length) return; // shouldn't happen — midnight is always in the future

        const target = Math.min(...candidates);
        const delayMs = target - nowPlaceMs;
        this._boundaryTimeout = setTimeout(() => this._startBoundaryPolling(), delayMs);
    }

    // Convert "HH:MM" + a place-time-as-if-UTC reference into the same-day place-time-as-if-UTC for that wall time.
    private _parseTimeTodayPlaceMs(hhmm: string, nowPlaceMs: number): number {
        const [h, m] = hhmm.split(':').map((s) => +s);
        const msPerDay = 86400_000;
        const startOfDayPlaceMs = nowPlaceMs - (nowPlaceMs % msPerDay);
        return startOfDayPlaceMs + (h * 60 + m) * 60_000;
    }

    // Returns the next 00:00 boundary as a place-time-as-if-UTC value, or null if the place
    // timezone isn't known (cached pre-rollout response).
    // When USE_PLACE_MIDNIGHT is false, midnight is computed in Israel time (the server currently
    // rolls Hebrew date at Israel midnight). Flip the constant once the server side is updated.
    private _nextMidnightPlaceMs(nowPlaceMs: number): number | null {
        const msPerDay = 86400_000;
        if (CalendarService.USE_PLACE_MIDNIGHT) {
            return nowPlaceMs - (nowPlaceMs % msPerDay) + msPerDay;
        }
        // Israel midnight: convert nowPlaceMs back to UTC, shift to Israel-as-if-UTC, find next 00:00 there.
        // Requires the place's tz offset to do the round-trip; if missing, can't schedule.
        const placeTzMs = this._currentPlaceTzOffsetMs();
        if (placeTzMs === null) return null;
        // Use the place's dstDiff > 0 as a proxy for "DST is currently in effect" (Israel and most other
        // observing places share the same DST window). Acceptable until USE_PLACE_MIDNIGHT is flipped.
        const place = this._currDayTimesAllData$.value?.standardTimes?.place;
        const dstActive = place ? +(place.dstDiff ?? 0) > 0 : false;
        const israelTzMs = (CalendarService.ISRAEL_TZ_HOURS + (dstActive ? 1 : 0)) * 3600_000;
        const utcNowMs = nowPlaceMs - placeTzMs;
        const nowIsraelMs = utcNowMs + israelTzMs;
        const nextIsraelMidnightMs = nowIsraelMs - (nowIsraelMs % msPerDay) + msPerDay;
        // Convert that Israel midnight back to place-time-as-if-UTC.
        return nextIsraelMidnightMs - israelTzMs + placeTzMs;
    }

    private _startBoundaryPolling(): void {
        if (AppGlobalsModule.isServer) return;
        const cached = this._currDayTimesAllData$.value;
        const placeId = cached?.standardTimes?.place?.id;
        const hebDate = cached?.extraDateData?.hebDate;
        if (!placeId || !hebDate) return;

        this._hebDateAtBoundaryStart = hebDate;
        this._boundaryPollStartedAt = Date.now();
        this._fetchAndCheckRollover(+placeId);

        if (this._boundaryPoller) clearInterval(this._boundaryPoller);
        this._boundaryPoller = setInterval(() => {
            if (Date.now() - this._boundaryPollStartedAt >= CalendarService.BOUNDARY_POLL_MAX_DURATION_MS) {
                // Give up after 5 minutes; reschedule against the next boundary.
                this._stopBoundaryPolling();
                this._scheduleNextBoundary();
                return;
            }
            // The fetch's success handler calls _stopBoundaryPolling on rollover detection, so
            // once the server flips hebDate the poller is cleared and this callback stops firing.
            this._fetchAndCheckRollover(+placeId);
        }, CalendarService.BOUNDARY_POLL_INTERVAL_MS);
    }

    private _stopBoundaryPolling(): void {
        if (this._boundaryPoller) {
            clearInterval(this._boundaryPoller);
            this._boundaryPoller = null;
        }
        this._hebDateAtBoundaryStart = null;
    }

    private _fetchAndCheckRollover(placeId: number): void {
        // Race avoidance: stamp now (before the async fetch) so the 1s watchdog sees a fresh
        // timestamp and doesn't push the cached (about-to-be-replaced) data during the in-flight
        // window. The successful fetch will stamp again via _setCurrDayTimesAllData.
        this._lastDataPushAt = Date.now();
        const url = `${this.ALL_DAILY_TIMES}?cacheVer=${this.CALENDAR_CACHE_VERSION}&place=${placeId}`;
        this._subscriptions.push(
            this._http
                .get<DayTimesAllData>(url, { observe: 'response', headers: new HttpHeaders({ noCache: 'true' }) })
                .pipe(take(1))
                .subscribe((response: HttpResponse<DayTimesAllData>) => {
                    const data = response.body;
                    if (!data) return;
                    this._updateClockSkew(response);
                    this._setCurrDayTimesAllData(data);
                    if (data.extraDateData?.hebDate && data.extraDateData.hebDate !== this._hebDateAtBoundaryStart) {
                        // Server has rolled over. Stop polling and schedule the next boundary.
                        this._stopBoundaryPolling();
                        this._scheduleNextBoundary();
                    }
                }),
        );
    }

    public loadShabbatTimesData() {
        this._subscriptions.push(
            this._http
                .get<ShabbatTimesData[]>(this.SHABBAT_TIMES_API_URL) //?addDays=15
                .pipe(take(1))
                .subscribe((shabbatTimesData) => this._setShabbatTimesData(shabbatTimesData)),
        );
    }

    /** the full Pesach schedule of the current Hebrew year; forceDays shifts "today" (the legacy page's debug parameter) */
    public getPesachTimes(forceDays = 0): Observable<PesachTimesData> {
        const url = forceDays ? `${this.PESACH_TIMES_API_URL}?forceDays=${forceDays}` : this.PESACH_TIMES_API_URL;
        return this._http.get<PesachTimesData>(url);
    }

    /** the memorial days (yahrzeit) list of /events/memorial, grouped by Hebrew month */
    public getMemorialDays(): Observable<MemorialDaysData> {
        return this._http.get<MemorialDaysData>(this.MEMORIAL_API_URL);
    }

    private _getDailyTimesWithModeTime(dailyTimes: TimeDate[]): TimeDate[] {
        // Todo: use in interval for set mode time every second
        const { nextTimeIdx, nextMainTimeIdx } = this._getNextTimeIdx(dailyTimes);
        return dailyTimes.map((dailyTime: TimeDate, i: number) => ({
            ...dailyTime,
            isMainTime: this.MAIN_TIMES_MAP[i],
            modeTime: this._getModeTime(nextMainTimeIdx, nextTimeIdx, i),
        }));
    }

    // Helpers
    public _getModeTime(nextMainTimeIdx: number, nextTimeIdx: number, currIdx: number): ModeTime {
        if (nextMainTimeIdx === currIdx) {
            return ModeTime.ClosestNextMainTime;
        } else if (nextTimeIdx === currIdx) {
            return ModeTime.ClosestNext;
        } else if (nextTimeIdx - 1 === currIdx) {
            return ModeTime.ClosestPrev;
        } else if (nextTimeIdx > currIdx) {
            return ModeTime.Prev;
        }
        return ModeTime.Next;
    }

    readonly MAIN_TIMES_MAP: Record<number, boolean> = {
        0: false, // עלות השחר
        1: false, //
        2: false,
        3: true,
        4: true,
        5: true,
        6: false,
        7: false,
        8: true,
        9: false,
        10: false,
        11: false,
        12: true,
        13: true,
        14: false,
        15: false,
    };

    private _timeToSecond(time: string): number {
        const [hour, min] = time.split(':');
        return ((+hour ? +hour : 24) * 60 + +min) * 60;
    }

    private _getSecFromStartDay() {
        // Use place-corrected time so modeTime tags are computed against the selected place's
        // wall clock, not the user's browser timezone. This also picks up the server-derived
        // clock skew correction.
        // Fallback path (cached pre-rollout response without tz fields): use the old browser-local
        // logic — wrong for non-local places, but matches today's behavior until the new fields ship.
        const msPerDay = 86400 * 1000;
        const placeMs = this._placeNow();
        if (placeMs !== null) {
            return (placeMs % msPerDay) / 1000;
        }
        return (Date.now() % msPerDay) / 1000 - new Date().getTimezoneOffset() * 60;
    }

    public _getNextTimeIdx(dailyTimes: TimeDate[]) {
        const dailyTimesAsSec = dailyTimes.map((dailyTime) => this._timeToSecond(dailyTime.value)).sort((a: number, b: number) => a - b);
        const secFromStartDay = this._getSecFromStartDay();
        const nextMainTimeIdx = dailyTimesAsSec.findIndex((timeAsSec, i) => this.MAIN_TIMES_MAP[i] && timeAsSec > secFromStartDay);
        const nextTimeIdx = dailyTimesAsSec.findIndex((timeAsSec) => timeAsSec > secFromStartDay);
        return { nextTimeIdx, nextMainTimeIdx };
    }

    private _updatePlaceFavCookie(place: PlaceLatLon) {
        if (!AppGlobalsModule.isServer) {
            let now = new Date();
            let expires = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
            this._doc.cookie = `times=Place=${place.id}&IL=${place.il}; path=/; expires=${expires.toUTCString()};`;
        }
    }

    private _currDayTimesAllData$ = new BehaviorSubject<DayTimesAllData | null>(null);
    public getCurrDayTimesAllData(): Observable<DayTimesAllData | null> {
        return this._currDayTimesAllData$.asObservable();
    }
    private _setCurrDayTimesAllData(dayTimesAllData: DayTimesAllData | null): void {
        if (dayTimesAllData) {
            dayTimesAllData.standardTimes.times = this._getDailyTimesWithModeTime(dayTimesAllData.standardTimes.times);
        }
        this._lastDataPushAt = Date.now();
        this._currDayTimesAllData$.next(dayTimesAllData);
    }

    private _shabbatTimesData$ = new BehaviorSubject<ShabbatTimesData[] | null>(null);
    public getShabbatTimesData(): Observable<ShabbatTimesData[] | null> {
        return this._shabbatTimesData$.asObservable();
    }
    private _setShabbatTimesData(shabbatTimesData: ShabbatTimesData[]): void {
        this._shabbatTimesData$.next(shabbatTimesData);
    }

    public addPlaceNameToPath(name: string) {
        if (!AppGlobalsModule.isServer) {
            // this._url.addParamToPath(name, 'place');
        }
    }

    public loadMonthlyCalendar(
        year: number,
        month: number,
        place?: PlaceLatLon,
        loazi: boolean = false,
        day: number = 0,
        isForUpdatePlaceFavCookie: boolean = true,
        idPlace?: number,
    ) {
        let placeId: number;
        if (idPlace) {
            placeId = idPlace;
        } else if (place) {
            if (isForUpdatePlaceFavCookie) {
                this._updatePlaceFavCookie(place);
            }
            placeId = +place.id;
        } else {
            const timesFromCookies = this.cookieService.get('times');
            placeId = timesFromCookies ? +timesFromCookies.split('&')[0].split('=')[1] : 156;
        }
        let dayParam = day > 0 ? `&dy=${day}` : '';
        let urlMonthlyCalendar = AppGlobalsModule.apiPath(
            `/calendar/calaj.aspx?cacheVer=${this.CALENDAR_CACHE_VERSION}&lng=${this.isHeb}&v=1&op=${
                Operation.MT
            }&yr=${year}&mn=${month}&pl=${placeId}&loazi=${loazi}${dayParam}&adjustLeapYear=${!loazi}&x=${this.getCode(
                Operation.MT,
                month,
                year,
                placeId,
                day,
                this._i18n.getLang(),
            )}`,
            '',
            '',
        );
        this._subscriptions.push(
            this._http
                .get<MonthlyCalendar>(urlMonthlyCalendar)
                .pipe(take(1))
                .subscribe((monthlyCalendar) => this._setMonthlyCalendar(monthlyCalendar)),
        );
    }

    private _monthlyCalendar$ = new BehaviorSubject<MonthlyCalendar | null>(null);
    public getMonthlyCalendar(): Observable<MonthlyCalendar | null> {
        return this._monthlyCalendar$.asObservable();
    }
    private _setMonthlyCalendar(monthlyCalendar: MonthlyCalendar): void {
        this._monthlyCalendar$.next(monthlyCalendar);
    }

    public getPlaces(): Observable<PlaceLatLon[]> {
        return this._places$;
    }

    // ------------------------------------------------------------------ the calendar pages' own requests (the port of
    // Content/scripts/Angular/Calendar/dataService.js; the exact queries are recorded in
    // tools/testing/calendar_tables/tables/api-urls.json and the answers in calendar_fixtures - docs/agents/calendar-port.md §4)

    /** The bot-gate key `CalAj.aspx.vb botsValidation` expects: the last character of each value given, missing ones dropped. */
    private _botKey(op: string, parts: { mn?: number | string; yr?: number | string; pl?: number | string; dy?: number | string }, lng: string): string {
        const last = (v: number | string | undefined) => (v === undefined || v === null || v === '' ? '' : this._lastChar(v));
        return `r${last(op)}${last(parts.mn)}h${last(parts.yr)}d${last(parts.pl)}${last(parts.dy)}k${last(lng)}`;
    }

    private _calajUrl(query: string): string {
        return AppGlobalsModule.apiPath(`/calendar/calaj.aspx?cacheVer=${this.CALENDAR_CACHE_VERSION}&v=1&${query}`, '', '');
    }

    /**
     * `op=dj` - one day's json for a civil date and a place (the selected day, the timesday page).
     * `place` is an id or a localized NAME (the .NET route 301s ids to names; `CalAj.aspx.vb`
     * resolves a name itself, dashes read as spaces - verified 2026-11-02 for dj and md), so a
     * `?place=<name>` url costs one call. The bot key takes the last character of the decoded value.
     */
    public getDayJson(date: CivilDate, place: number | string): Observable<DayJson> {
        const lng = this._i18n.getLang();
        const x = this._botKey('dj', { mn: date.month, yr: date.year, pl: place, dy: date.day }, lng);
        return this._http.get<DayJson>(
            this._calajUrl(`op=dj&pl=${encodeURIComponent(String(place))}&yr=${date.year}&mn=${date.month}&dy=${date.day}&sv=false&lng=${lng}&x=${x}`),
        );
    }

    /**
     * `op=md&js=true` - a Hebrew month's grid plus the requested day's json. `hebrew` says whether
     * `year/month/day` are a Hebrew date (month = the 13-slot api month, see calendar-logic/hebrew-months)
     * or a civil one; the `dh*` offsets step from that date and the server resolves the roll
     * (calendar-logic/day-step builds them).
     */
    public getMonthJson(
        request: { year: number | string; month: number; day: number; hebrew: boolean; dhy?: number; dhm?: number; dhd?: number },
        place: number | string,
    ): Observable<MonthJson> {
        const lng = this._i18n.getLang();
        const { year, month, day, hebrew, dhy = 0, dhm = 0, dhd = 0 } = request;
        const x = this._botKey('md', { mn: month, yr: encodeURIComponent(String(year)), pl: place, dy: day }, lng);
        return this._http.get<MonthJson>(
            this._calajUrl(
                `op=md&pl=${encodeURIComponent(String(place))}&yr=${encodeURIComponent(String(year))}&mn=${month}&dy=${day}&hb=${hebrew}&dhy=${dhy}&dhm=${dhm}&dhd=${dhd}&sv=false&lng=${lng}&js=true&x=${x}`,
            ),
        );
    }

    /** `op=md&short=true&cal=je|gr` - the date picker's month: dates only; `cal=gr` answers a CIVIL month. */
    public getPickerMonth(request: { year: number | string; month: number; day?: number; hebrew: boolean; civilGrid: boolean }, placeId = DEFAULT_PLACE): Observable<PickerMonthJson> {
        const lng = this._i18n.getLang();
        const { year, month, day = 1, hebrew, civilGrid } = request;
        const x = this._botKey('md', { mn: month, yr: encodeURIComponent(String(year)), pl: placeId, dy: day }, lng);
        return this._http.get<PickerMonthJson>(
            this._calajUrl(
                `op=md&pl=${placeId}&yr=${encodeURIComponent(String(year))}&mn=${month}&dy=${day}&hb=${hebrew}&dhy=0&dhm=0&dhd=0&sv=false&lng=${lng}&short=true&cal=${civilGrid ? 'gr' : 'je'}&js=false&x=${x}`,
            ),
        );
    }

    /**
     * `op=ys` - the Shabbatot and holidays of a Hebrew year for a place; `yearOffset` ±1 steps a year.
     * `place` is an id or a localized place name (`CalAj.aspx.vb` resolves `pl` with
     * `arrPlaceNew.PlaceIdByName`, dashes read as spaces), so a `?place=<name>` url needs no lookup first.
     */
    public getShabatotYear(hebYear: number | string, place: number | string, yearOffset = 0): Observable<ShabatotYearJson> {
        const lng = this._i18n.getLang();
        // the bot key reads the DECODED `pl` (its last character) but the uri-ESCAPED `yr` (CalAj.aspx.vb botsValidation)
        const x = this._botKey('ys', { yr: encodeURIComponent(String(hebYear)), pl: String(place) }, lng);
        return this._http.get<ShabatotYearJson>(
            this._calajUrl(`op=ys&pl=${encodeURIComponent(String(place))}&yr=${encodeURIComponent(String(hebYear))}&dyr=${yearOffset}&lng=${lng}&sv=false&x=${encodeURIComponent(x)}`),
        );
    }

    private _getDayDediCalMap(hebYear: number, hebMonth: number, dedicationType: DedicationType, lang?: string) {
        lang = lang || 'heb';
        this._http
            .get<DediDayMapsPerCurrAndNextMonthModel>(
                AppGlobalsModule.apiPath(`dedication/GetDedicationDates?hebYear=${hebYear}&hebMonth=${hebMonth}&dedicationType=${dedicationType}&lang=${lang}`),
            )
            .pipe(take(1))
            .subscribe((dayDediCalMap) => {
                this._setDediDayMapsPerCurrAndNextMonth(dayDediCalMap);
            });
    }

    private _dediDayMapsPerCurrAndNextMonth$ = new Subject<DediDayMapsPerCurrAndNextMonthModel | null>();
    public getDediDayMapsPerCurrAndNextMonth(): Observable<DediDayMapsPerCurrAndNextMonthModel | null> {
        return this._dediDayMapsPerCurrAndNextMonth$.asObservable();
    }
    private _setDediDayMapsPerCurrAndNextMonth(dediDayMapsPerCurrAndNextMonth: DediDayMapsPerCurrAndNextMonthModel | null): void {
        this._dediDayMapsPerCurrAndNextMonth$.next(dediDayMapsPerCurrAndNextMonth);
    }

    public hebrewYearInLetters(year: number): string {
        const letters: Record<number, string> = {
            1: 'א',
            2: 'ב',
            3: 'ג',
            4: 'ד',
            5: 'ה',
            6: 'ו',
            7: 'ז',
            8: 'ח',
            9: 'ט',
            10: 'י',
            20: 'כ',
            30: 'ל',
            40: 'מ',
            50: 'נ',
            60: 'ס',
            70: 'ע',
            80: 'פ',
            90: 'צ',
            100: 'ק',
            200: 'ר',
            300: 'ש',
            400: 'ת',
        };

        let n = year % 1000; // normally only last 3 digits are written
        let parts: string[] = [];

        // Handle hundreds (400 can repeat)
        while (n >= 400) {
            parts.push(letters[400]);
            n -= 400;
        }
        if (n >= 100) {
            const h = Math.floor(n / 100) * 100;
            parts.push(letters[h]);
            n %= 100;
        }

        // Handle tens
        if (n >= 90) {
            parts.push(letters[90]);
            n -= 90;
        }
        if (n >= 10) {
            const t = Math.floor(n / 10) * 10;
            if (t > 0) {
                parts.push(letters[t]);
            }
            n %= 10;
        }

        // Handle units
        if (n > 0) {
            parts.push(letters[n]);
        }

        let str = parts.join('');

        // Special case correction: avoid writing יה (15) or יו (16)
        str = str.replace('יה', 'טו').replace('יו', 'טז');

        // Add gershayim (״) before last letter if more than one letter
        if (str.length > 1) {
            str = str.slice(0, -1) + '״' + str.slice(-1);
        } else {
            str += '׳';
        }

        return str;
    }

    public getHebDate(date = Date.now()): DateParams {
        const dateStr = new Date(date).toLocaleDateString(`en-u-ca-hebrew`);
        //numFormat means the format returned is only numbers with the month first like so
        //3/10/5783 AM
        //this means the 3rd day of Kislev
        let numFormat = dateStr.includes('/');
        const splitStr = numFormat ? '/' : ' ';

        const dateParams = dateStr.replace(/Adar I/g, 'AdarI').split(splitStr);

        //we recheck the numFormat with a more failsafe method  that makes sure the 2nd param now that we have dateParams array
        numFormat = typeof dateParams[1] === 'number' || !isNaN(parseInt(dateParams[1], 10));

        const day = numFormat ? +dateParams[1] : +dateParams[0];
        const month = numFormat ? parseInt(dateParams[0], 10) : this.getMonthByMonthStr(dateParams[1]);
        const year = numFormat ? /[0-9]+/.exec(dateParams[2])![0] : dateParams[2];
        if (numFormat && month > 13) {
            throw new Error('problem with using backup number format for getting hebrew date');
        }
        return {
            day: +day,
            month,
            year: +year,
            yearInLetters: this.hebrewYearInLetters(+year),
        };
    }

    public getHebDateString(date: Date): string {
        // https://stackoverflow.com/a/71267156
        if (!this.isHeb) {
            return new Intl.DateTimeFormat('en-u-ca-hebrew').format(date);
        } else {
            return new Intl.DateTimeFormat('he-u-ca-hebrew', {
                weekday: 'long',
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
            }).format(date);
        }
    }

    private getMonthByMonthStr(currMonthStr: string): number {
        return HebMonths[currMonthStr as keyof typeof HebMonths];
    }

    public getFullNameForMonthlyCalendar(titleId: string, heb: boolean): string {
        switch (titleId) {
            case 'Alot':
                return heb ? 'עלות השחר' : 'Alot Hashachar';
            case 'Tfilin':
                return heb ? 'זמן טלית ותפילין' : 'Talit & Tefillin time';
            case 'Sunrise':
                return heb ? 'הנץ החמה' : 'Sunrise';
            case 'SunriseAtPlane':
                return heb ? 'הנץ החמה במישור' : 'Astronomical sunrise';
            case 'EndKS_MGA':
                return heb ? 'סו"ז ק"ש למג"א' : 'Last Kriyat Shma time for MGA';
            case 'EndKS_GRA':
                return heb ? 'סו"ז ק"ש לגר"א' : 'Last Kriyat Shma time for GRA';
            case 'EndTfila_MGA':
                return heb ? 'סו"ז תפילה למג"א' : 'Last Tefilla time for MGA';
            case 'EndTfile_GRA':
                return heb ? 'סו"ז תפילה לגר"א' : 'Last Tefilla time for GRA';
            case 'Minha_Gdola':
                return heb ? 'מנחה גדולה' : 'Mincha Gedola';
            case 'Minha_Ktana':
                return heb ? 'מנחה קטנה' : 'Mincha Ketana';
            case 'PlagMinha':
                return heb ? 'פלג המנחה' : 'Plag Hamincha';
            case 'Night':
                return heb ? 'צאת הכוכבים' : 'Tzet Hakochavim';
            case 'Night_RT':
                return heb ? 'צאת הכוכבים לר"ת' : 'Rabenu Tam Tezt Hakochavim';
            case 'Limud_Bavli':
                return heb ? 'דף יומי בבלי' : 'Daf yomi - Bavli';
            case 'Limud_Yerushalmi':
                return heb ? 'דף יומי ירושלמי' : 'Daf Yomi - Yerushalmi';
            case 'Limud_Rambam1':
                return heb ? 'רמב"ם פרק יומי' : 'Rambam - 1 Chapter a Day';
            case 'Limud_Rambam3':
                return heb ? 'רמב"ם ג\' פרקים' : 'Rambam - 3 Chapter';
            case 'Limud_MishnaBrura':
                return heb ? 'עמוד יומי במשנה ברורה' : 'Amud Yomi - Mishna Brura';
            case 'Midnight':
                return heb ? 'חצות' : 'Midnight';
            case 'Sunset':
                return heb ? 'שקיעה' : 'Sunset';
            case 'DayOfWeek':
                return heb ? 'יום בשבוע' : 'Day Of Week';
            case 'HebDayInMonth':
                return heb ? 'יום בחודש' : 'Hebrew Day In Month';
            case 'LoaziDate':
                return heb ? 'תאריך לועזי' : 'Date';
            default:
                return '';
        }
    }

    ngOnDestroy(): void {
        if (this._dailyTimeInterval) {
            clearInterval(this._dailyTimeInterval);
            this._dailyTimeInterval = null;
        }
        if (this._boundaryTimeout) {
            clearTimeout(this._boundaryTimeout);
            this._boundaryTimeout = null;
        }
        if (this._boundaryPoller) {
            clearInterval(this._boundaryPoller);
            this._boundaryPoller = null;
        }
        this._util.unsubscribe(this._subscriptions);
        this._util.unsubscribeSubject([
            this._currDatepickerCal$,
            this._dediDayMapsPerCurrAndNextMonth$,
            this._currDayTimesAllData$,
            this._monthlyCalendar$,
            this._shabbatTimesData$,
        ]);
    }
}
export interface DediDayMapsPerCurrAndNextMonthModel {
    current: DayDediCalMapModel;
    next?: DayDediCalMapModel;
}
export interface DayDediCalMapModel {
    [key: number]: string;
}

export interface DateParams {
    day?: number;
    month: number;
    year: number;
    yearInLetters?: string;
}

export interface HebMonth {
    key: HebMonths;
    value: string;
}

export interface DedicationDatePickerTitle {
    key: DedicationType;
    value: string;
}

export enum HebMonths {
    Tishri = 1,
    Heshvan = 2,
    Kislev = 3,
    Tevet = 4,
    Shevat = 5,
    AdarI = 6,
    AdarII = 7,
    Adar = 7, // or 6
    Nisan = 8,
    Iyar = 9,
    Sivan = 10,
    Tamuz = 11,
    Av = 12,
    Elul = 13,
    תשרי = 1,
    חשון = 2,
    בכסלו = 3,
}

export interface EngMonth {
    key: EngMonths;
    value: string;
}

export enum EngMonths {
    January = 1,
    February = 2,
    March = 3,
    April = 4,
    May = 5,
    June = 6,
    July = 7,
    August = 8, // or 6
    September = 9,
    October = 10,
    November = 11,
    December = 12,
}

export enum DedicationType {
    NONE = -1,
    SITE_DAILY = 0,
    SITE_PREMIUM = 1,
    DAILY_HALACHA = 2,
    KADDISH_MONTH = 3,
}

export interface PlaceLatLon extends BasePlace, SelctableItem {
    lat: string; //  "31.318"
    lon: string; // "34.617",
}

export enum Operation {
    MD = 'md',
    DJ = 'dj',
    MT = 'mt',
    ML = 'ml',
}

export enum CalendarType {
    HEBREW = 0,
    GREGORIAN = 1,
}

export interface ExtraDateData {
    hebDate: string;
    omer: string;
}

export interface DayTimesAllData {
    holidayTimes: HolidayTimes;
    standardTimes: StandardTimes;
    extraDateData?: ExtraDateData; // <-- add this if not present
    // Place-local wall-clock time the response was generated at, ISO 8601 without an offset
    // (e.g. "2026-04-28T19:53:42"). Combined with the HTTP `Age` header to recover the true
    // current place-time, used to correct for browser clock skew and schedule shkia/midnight refreshes.
    datetime?: string;
}

export interface HolidayTimes {
    type: HolidayTimesType; // 5
    holidayDayData: {
        curHebDate: string; // "מוצ"ש, ח' מנחם אב ה'תשפ"א",
        curLoaziDate: string; // "17/7/2021",
        endMoedHebDate: string; // "",
        endMoedLoaziDate: string; // "",
        curMoedName: string; // "תשעה באב",
        zmanimCount: number; // 4,
        hebTimesNames: string[]; // "כניסת הצום", "צאת הצום", null, null
        engTimesNames: string[]; // "Fast begins", "Fast ends", "Hebrew date", "Gregorian date"
        what: 0;
        timesDat: TimeTypeHoliday[];
    };
    Times: TimeTypeHoliday[][];
    dateChecked: string; // "2021-07-07T16:09:09.0433761+03:00"
}

interface TimeTypeHoliday {
    val: string; // "19:49",
    hebName: string; // "כניסת הצום",
    engName: string; // "Fast begins",
    timeCode: string; // "17",
    timePlaceCode: string; // "156"
}

export interface StandardTimes {
    date: {
        heb: HebDate;
        eng: EngDate;
        weekday: string; // "יום שישי"
    };
    daycode: string; // "יום שישי"
    // Optional: the server omits this key entirely on non-special days (isChag returns no codes),
    // and it can be absent even when holidayTimes.type is a special value (pre-fast look-ahead days).
    specials?: Special[];
    shabat: ShabatDate;
    moed: ShabatMoedTime[];
    place: BasePlace;
    times: TimeDate[];
    limud: LimudDate[];
    memorial: { title: string; link: string }[];
    events: [];
    omer?: string;
    molad?: any;
    /** "אומרים" / "אין אומרים" - the day's tachanun rule (op=dj) */
    tachanun?: string;
}

export interface ShabbatTimesData {
    placeName: string;
    placeId: number;
    il: boolean;
    latitude: number;
    longitude: number;
    shabbatStart: string;
    shabbatEnd: string;
    shabbatEndRabeinuTam: string;
    priority?: number;
    isChag?: boolean;
    isChagMotzeiShabbat?: boolean;
    endTimeIsShabbatStart?: boolean;
}

/** api/calendar/pesachtimes - the Pesach schedule, already worded per language by the server */
export interface MemorialDaysData {
    months: MemorialMonth[];
}

export interface MemorialMonth {
    /** HebMonths numbering (Tishri=1 .. AdarI=6, Adar=7 .. Elul=13) */
    month: HebMonths;
    rows: MemorialRow[];
}

export interface MemorialRow {
    scheduleId: number;
    title: string;
    link?: string;
    day: number;
    month: HebMonths;
    year: number;
    /** the next memorial day on the civil calendar, yyyy-MM-dd */
    nextDate: string;
    googleCalendarUrl: string;
}

export interface BarMitzvaData {
    BirthdayHeb: string;
    BirthdayEn: string;
    BarmitzvaDateHeb: string;
    BarmitzvaDateEn: string;
    ShabatDateHeb: string;
    ShabatDateEn: string;
    ShabatData: ShabatDate;
    /** the same shabbat abroad - null when its parasha is the one read in Israel */
    ShabatDataChul: ShabatDate | null;
}

export interface PesachTimesData {
    year: number;
    yearText: string;
    summerClock: boolean;
    /** the designed printable pdf: always linked; its year may lag the page year until the new file is uploaded */
    pdfUrl: string;
    pdfYearText: string;
    sections: PesachTimesSection[];
}

export interface PesachTimesSection {
    title: string;
    dateText?: string;
    text?: string;
    rows: PesachTimesRow[];
}

export interface PesachTimesRow {
    label?: string;
    times: PesachPlaceTime[];
}

export interface PesachPlaceTime {
    place: string;
    time: string;
}

interface HebDate {
    hebdate: string; // "כ"ב תמוז ה'תשפ"א",
    year: string; // "ה'תשפ"א",
    month: string; // "תמוז",
    day: string; // "כ"ב",
    yearNumber: string; // "5781",
    monthNumber: string; // "10",
    dayNumber: string; // "22",
    yesrDescription: string; // "שנה פשוטה, ו' לשמיטה ",
    meubar: string; // "פשוטה"
}
interface EngDate {
    ldate: string; // "2/7/2021",
    year: string; // "2021",
    month: string; // "7",
    day: string; // "2"
}

interface Special {
    key: number; // 113024,
    val: string; // "בין המצרים",
    cat: number; // 0
}
export interface BasePlace {
    name: string; // "ירושלים",
    id: string; // "156",
    il?: string; // "True"
    timezone?: string; // hours offset from GMT without DST, e.g. "2"
    dstDiff?: string; // hours to add when DST is active, e.g. "1" (or "0"/missing when not in DST)
}
export interface TimeDate {
    name: string; // "עלות השחר 90 דקות(מעלות)",
    value: string; // "3:46"
    isMainTime?: boolean;
    modeTime?: ModeTime;
}
interface LimudDate {
    name: string; // "דף יומי בבלי",
    value: string; // "יומא פ"ב",
    link: string; // "/tags/הדף-היומי"
}
export interface ShabatDate {
    midrash_href: string; // "/midrash/category/54",
    shabat_name: string; //"פנחס",
    shabat_prefix: string; // " שבת",
    times: ShabatMoedTime[];
    haftara: { type: string; name: string }[];
    skiah: string; // "19:53",
    hebDate: string; // "כ"ג תמוז ה'תשפ"א",
    loaziDate: string; // "3/7/2021",
    avot: string; // "פרקי אבות א"
    // true when the upcoming chag (holidayTimes) falls on this very Shabbat; the hp then skips its
    // separate moed block. Optional: older cached responses lack it.
    chagOnShabbat?: boolean;
}

interface ShabatMoedTime extends TimeDate {
    today: boolean;
}

export enum ModeTime {
    // Todo: should reflect the current time by approximately a few minutes
    // Current = 'current'
    Next = 'next',
    ClosestNext = 'closest-next',
    ClosestNextMainTime = 'closest-main-time-next',
    Prev = 'prev',
    ClosestPrev = 'closest-prev',
}

// From times.cs file in calEvents project line 32
export enum HolidayTimesType {
    noZmanim = 0, // Don't show
    onlyShabat = 1, // Don't show
    ordinaryDay = 2, // Don't show
    chag = 3,
    tzom = 4,
    tzom9beav = 5,
    fulDate = 6, // Don't show
    chanukah = 7,
    pessach_A = 8,
    pessach_B = 9,
    baseOrdinaryAndShabat = 10, // Don't show
}

export interface DailyTimeHeader {
    mainDailyTimes: TimeDate[];
    closestTimeDay: TimeDate;
    currPlace: BasePlace;
}

export interface MonthlyCalendar {
    times: string[][];
    names: MonthlyTitle[];
    date: { heb: HebDateMonthly; jd: number };
    place: BasePlace;
}

export interface MonthlyTitle {
    name: string; // "יום בחודש",
    id: string; // "HebDayInMonth",
    type: string; // "type-date"
}

export interface HebDateMonthly {
    year: number; // 5783,
    yearText: string; //"ה'תשפ\"ג",
    month: number; //7,
    monthName: string; // ניסן
    meubar: boolean; //false
}

/*
public getStandardTimesByEngDate(
    year: number = new Date().getFullYear(),
    month: number = new Date().getMonth() + 1,
    currDay: number = new Date().getDate()
): Observable<StandardTimes> {
    return this._http
        .get<any>(this._getCalApi(Operation.MD, year, month, currDay, DEFAULT_PLACE, true, false))
        .pipe(map(monthJson => monthJson.times));
}
*/
